import { useState, useEffect, useContext, createContext } from 'react';
import axios from './axios';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setUserRole(parsedUser.role);
       
        // Charger les permissions depuis l'API
        await loadUserPermissions(token, parsedUser._id);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation:', error);
        logout();
      }
    }
   
    setLoading(false);
  };

  const loadUserPermissions = async (token, userId) => {
    try {
      // Essayer d'abord de récupérer les permissions de l'API
      const response = await axios.get('/api/users/me/permissions', {
        headers: { Authorization: `Bearer ${token}` }
      });
     
      if (response.data.success) {
        setPermissions(response.data.permissions || []);
        setUserPermissions(response.data.permissions || []);
      } else {
        // Fallback aux permissions par défaut selon le rôle
        setDefaultPermissions();
      }
    } catch (error) {
      console.error('Erreur chargement permissions:', error);
      // Fallback aux permissions par défaut
      setDefaultPermissions();
    }
  };

  const setDefaultPermissions = () => {
    const defaultRolePermissions = {
      'admin': [
        'dashboard:view',
        'clients:read', 'clients:create', 'clients:update', 'clients:delete',
        'achats:read', 'achats:create', 'achats:update', 'achats:delete',
        'ventes:read', 'ventes:create', 'ventes:update', 'ventes:delete',
        'chauffeurs:read', 'chauffeurs:create', 'chauffeurs:update', 'chauffeurs:delete',
        'fournisseurs:read', 'fournisseurs:create', 'fournisseurs:update', 'fournisseurs:delete',
        'vehicules:read', 'vehicules:create', 'vehicules:update', 'vehicules:delete',
        'personnels:read', 'personnels:create', 'personnels:update', 'personnels:delete',
        'admin:users:manage', 'admin:roles:manage', 'admin:permissions:manage',
        'reports:view', 'reports:export',
        'settings:manage',
        'profile:view', 'profile:update'
      ],
      'manager': [
        'dashboard:view',
        'clients:read', 'clients:create', 'clients:update',
        'achats:read', 'achats:create', 'achats:update',
        'ventes:read', 'ventes:create', 'ventes:update',
        'chauffeurs:read', 'chauffeurs:create', 'chauffeurs:update',
        'fournisseurs:read', 'fournisseurs:create', 'fournisseurs:update',
        'vehicules:read', 'vehicules:create', 'vehicules:update',
        'personnels:read', 'personnels:create', 'personnels:update',
        'reports:view', 'reports:export',
        'profile:view', 'profile:update'
      ],
      'user': [
        'dashboard:view',
        'clients:read',
        'achats:read',
        'ventes:read',
        'chauffeurs:read',
        'fournisseurs:read',
        'vehicules:read',
        'personnels:read',
        'profile:view', 'profile:update'
      ]
    };
   
    const userDefaultPermissions = defaultRolePermissions[userRole] || defaultRolePermissions.user;
    setPermissions(userDefaultPermissions);
    setUserPermissions(userDefaultPermissions);
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
     
      if (response.data.success) {
        const { token, user } = response.data;
       
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
       
        setUser(user);
        setUserRole(user.role);
       
        // Charger les permissions après login
        await loadUserPermissions(token, user._id);
       
        return { success: true, user };
      }
     
      return { success: false, message: response.data.message };
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur de connexion'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setUser(null);
    setUserRole(null);
    setPermissions([]);
    setUserPermissions([]);
  };

  const hasPermission = (requiredPermission) => {
    if (!user) return false;
   
    // Les administrateurs ont tous les droits
    if (userRole === 'admin') {
      return true;
    }
   
    // Vérifier les permissions spécifiques
    if (permissions && permissions.length > 0) {
      return permissions.includes(requiredPermission);
    }
   
    return false;
  };

  const hasRole = (requiredRole) => {
    if (!userRole) return false;
    return userRole === requiredRole;
  };

  const can = (action, resource) => {
    const permission = `${resource}:${action}`;
    return hasPermission(permission);
  };

  const getCurrentUser = () => {
    return user;
  };

  const refreshPermissions = async () => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
   
    if (token && storedUser) {
      const parsedUser = JSON.parse(storedUser);
      await loadUserPermissions(token, parsedUser._id);
    }
  };

  const getUserPermissions = () => {
    return userPermissions;
  };

  const value = {
    user,
    userRole,
    permissions,
    loading,
    login,
    logout,
    hasPermission,
    hasRole,
    can,
    getCurrentUser,
    refreshPermissions,
    getUserPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};